/**
 * Compare Neighbourhoods panel — up to 4 pinned areas.
 *
 * Variants:
 *   • variant="page"    — full-screen /compare-neighbourhoods route
 *                          (Save · Share · Best-Match strip enabled)
 *   • variant="inline"  — compact drawer on the property detail page
 *                          (2 pins max, no Save/Share/BestMatch)
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, Trophy, Sparkles, Loader2, ArrowRight, Save, Check,
          Share2, Download, Plus, Footprints, TrainFront, Utensils,
          GraduationCap, ShoppingCart, MapPin, Home } from 'lucide-react';
import WishlistHeart from './WishlistHeart';
import { useMarket } from '../hooks/useMarket';

const MAX_PINS = 4;

const SCORES = [
  { key: 'walk',     label: 'Walk',     Icon: Footprints,    colour: '#7042cc' },
  { key: 'transit',  label: 'Transit',  Icon: TrainFront,    colour: '#0284c7' },
  { key: 'food',     label: 'Food',     Icon: Utensils,      colour: '#b45309' },
  { key: 'schools',  label: 'Schools',  Icon: GraduationCap, colour: '#f59e0b' },
  { key: 'everyday', label: 'Everyday', Icon: ShoppingCart,  colour: '#16a34a' },
];

const _scoreColour = (s) => {
  if (s == null) return 'text-gray-400';
  if (s >= 7)   return 'text-emerald-700';
  if (s >= 4)   return 'text-amber-700';
  return              'text-rose-700';
};

// Dedupe repeated parts in a Nominatim display_name — handles both
// consecutive ("Rochfortbridge, Rochfortbridge, ...") and non-
// consecutive dupes ("A, X, A, B, ..."). Case-insensitive.
const _shortLabel = (raw) => {
  if (!raw) return '';
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  const seen = new Set();
  const deduped = parts.filter((p) => {
    const k = p.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return deduped.slice(0, 3).join(', ');
};

// ── Autocomplete area picker ──────────────────────────────────────────
const AreaPicker = ({ label, onPick, initial, disabled, placeholder }) => {
  const [q, setQ] = useState(initial?.label || '');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hits, setHits] = useState([]);

  useEffect(() => { if (initial?.label) setQ(initial.label); }, [initial?.label]);

  const search = useCallback(async (text) => {
    if (!text.trim()) { setHits([]); return; }
    setBusy(true);
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(text)}`,
        { headers: { 'Accept-Language': 'en' } },
      );
      const d = await r.json();
      setHits(d || []);
    } catch { setHits([]); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    const id = setTimeout(() => { if (q.length >= 2) search(q); }, 350);
    return () => clearTimeout(id);
  }, [q, search]);

  return (
    <div className="relative">
      <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">
        {label}
      </label>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        <input
          type="text" value={q}
          disabled={disabled}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={placeholder || 'e.g. Ballinteer, Dublin'}
          className="w-full h-9 pl-8 pr-8 text-sm rounded-md ring-1 ring-gray-200 focus:ring-[#7042cc] outline-none disabled:bg-gray-50"
          data-testid={`compare-input-${label.toLowerCase().replace(/\s+/g, '-')}`}
        />
        {busy && <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-gray-400" />}
        {!busy && q && !disabled && (
          <button
            type="button"
            onClick={() => { setQ(''); setHits([]); onPick(null); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
            data-testid={`compare-clear-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && hits.length > 0 && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ul className="absolute left-0 right-0 top-full mt-1 z-50 max-h-60 overflow-auto rounded-md bg-white shadow-lg ring-1 ring-gray-200 text-xs"
               data-testid={`compare-suggests-${label.toLowerCase().replace(/\s+/g, '-')}`}>
            {hits.map((h) => (
              <li key={h.place_id}>
                <button
                  type="button"
                  onClick={() => {
                    const short = _shortLabel(h.display_name);
                    setQ(short);
                    setOpen(false);
                    onPick({ label: short, lat: parseFloat(h.lat), lng: parseFloat(h.lon) });
                  }}
                  className="w-full text-left px-2 py-1.5 hover:bg-gray-50 flex items-start gap-1.5"
                >
                  <MapPin className="h-3 w-3 mt-0.5 text-gray-400 shrink-0" />
                  <span className="leading-tight">{h.display_name}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

// ── Fetch scores + vibe for N picks in one hook ───────────────────────
const useAreaDataMany = (picks) => {
  const [items, setItems] = useState([]);   // array of {scores, vibe}
  const [loading, setLoading] = useState(false);
  const key = picks.map((p) => p ? `${p.lat.toFixed(3)},${p.lng.toFixed(3)}` : '-').join('|');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const base = process.env.REACT_APP_BACKEND_URL;
    const fetchOne = async (pick) => {
      if (!pick || pick.lat == null || pick.lng == null) return { scores: null, vibe: null };
      const scoresF = () => fetch(`${base}/api/neighbourhood/scores?lat=${pick.lat}&lng=${pick.lng}`)
        .then((r) => r.ok ? r.json() : null).catch(() => null);
      const vibeF = () => fetch(`${base}/api/neighbourhood/vibe?lat=${pick.lat}&lng=${pick.lng}`)
        .then((r) => r.ok ? r.json() : null).catch(() => null);
      let [s, v] = await Promise.all([scoresF(), vibeF()]);
      if (!s || s.unavailable || !v?.vibe) {
        await new Promise((r) => setTimeout(r, 1500));
        const [s2, v2] = await Promise.all([
          (!s || s.unavailable) ? scoresF() : Promise.resolve(s),
          !v?.vibe                ? vibeF()   : Promise.resolve(v),
        ]);
        s = s2 || s;
        v = v2 || v;
      }
      return { scores: s, vibe: v?.vibe || null };
    };
    Promise.all(picks.map(fetchOne)).then((rows) => {
      if (!cancelled) { setItems(rows); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [key]);

  return { items, loading };
};

// ── One area column ───────────────────────────────────────────────────
const AreaColumn = ({ pick, data, allScores, index, onRemove, allowRemove }) => (
  <div className="bg-white rounded-lg ring-1 ring-gray-200 p-4 relative"
        data-testid={`compare-column-${index}`}>
    {allowRemove && (
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-2 right-2 text-gray-400 hover:text-rose-600 p-1"
        aria-label="Remove this area"
        data-testid={`compare-remove-${index}`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    )}
    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">
      Area {String.fromCharCode(65 + index)}
    </p>
    <div className="flex items-center gap-1 mb-3">
      <p className="text-base font-semibold text-gray-900 truncate flex-1"
          title={pick?.label}>
        {pick?.label || 'Pick an area →'}
      </p>
      {pick && (
        <WishlistHeart lat={pick.lat} lng={pick.lng} label={pick.label} size="md" />
      )}
    </div>
    {!pick && (
      <p className="text-xs text-gray-400 italic">
        Search a suburb, town or postcode to load its scores and vibe.
      </p>
    )}
    {pick && data?.scores?.unavailable && (
      <p className="text-xs text-gray-500 italic"
          data-testid={`compare-unavailable-${index}`}>
        Scoring temporarily unavailable — try refreshing in a minute.
      </p>
    )}
    {pick && !data?.scores?.unavailable && (
      <>
        <ul className="space-y-1.5 mb-3">
          {SCORES.map(({ key, label, Icon, colour }) => {
            const s = data?.scores?.[key]?.score;
            // Winner across all N pinned areas (unique top score only).
            const all = allScores.map((sc) => sc?.[key]?.score).filter((v) => v != null);
            const top = all.length ? Math.max(...all) : null;
            const isSoleWinner = s != null && top != null && s === top
              && all.filter((v) => v === top).length === 1;
            return (
              <li key={key} className="flex items-center gap-2"
                   data-testid={`compare-${index}-row-${key}`}>
                <Icon className="h-3.5 w-3.5" style={{ color: colour }} />
                <span className="flex-1 text-sm text-gray-700">{label}</span>
                <span className={`font-mono font-bold ${_scoreColour(s)}`}>
                  {s != null ? s.toFixed(1) : '—'}
                </span>
                {isSoleWinner
                  ? <Trophy className="h-3.5 w-3.5 text-amber-500" aria-label="wins on this score" />
                  : <span className="w-3.5" />}
              </li>
            );
          })}
        </ul>
        {data?.vibe && (
          <div className="rounded-md bg-[#f5efff] ring-1 ring-[#7042cc]/20 p-2.5">
            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#5b35a8] mb-1">
              <Sparkles className="h-3 w-3" /> Vibe
            </div>
            <p className="text-[12px] text-gray-800 leading-snug">{data.vibe}</p>
          </div>
        )}
      </>
    )}
  </div>
);

// ── Best-match tabbed strip (multi-area) ─────────────────────────────
const _BBOX_HALF = 0.05;

const BestMatchStrip = ({ picks, dataItems }) => {
  const market = useMarket();
  const [axis, setAxis] = useState('walk');
  const validPicks = picks.filter(Boolean);
  const winnerIdx = useMemo(() => {
    const vals = validPicks.map((_, i) => dataItems[i]?.scores?.[axis]?.score)
      .map((v, i) => ({ i, v }))
      .filter((r) => r.v != null);
    if (!vals.length) return null;
    const top = Math.max(...vals.map((r) => r.v));
    const winners = vals.filter((r) => r.v === top);
    return winners.length === 1 ? winners[0].i : null;
  }, [dataItems, axis, validPicks.length]);

  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (winnerIdx == null || !validPicks[winnerIdx]) { setItems(null); return; }
    const winnerPick = validPicks[winnerIdx];
    const floor = dataItems[winnerIdx]?.scores?.[axis]?.score;
    if (floor == null) return;

    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({
      [`min_${axis}_score`]: String(Math.max(0, Math.floor(floor - 1))),
      limit: '30',
    });
    if (market?.name) params.set('country', market.name);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/properties?${params.toString()}`)
      .then((r) => r.ok ? r.json() : [])
      .then((rows) => {
        if (cancelled) return;
        const filtered = (rows || []).filter((p) => {
          const loc = p.location || {};
          const la = loc.lat ?? loc.latitude;
          const lo = loc.lng ?? loc.longitude;
          if (la == null || lo == null) return false;
          return Math.abs(la - winnerPick.lat) <= _BBOX_HALF
              && Math.abs(lo - winnerPick.lng) <= _BBOX_HALF;
        }).slice(0, 3);
        setItems(filtered);
      })
      .catch(() => setItems([]))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [axis, winnerIdx, validPicks, dataItems]);

  if (validPicks.length < 2) return null;

  return (
    <div className="mt-4 bg-white rounded-lg ring-1 ring-gray-200 p-4"
          data-testid="compare-best-match-strip">
      <div className="flex items-center gap-2 mb-3">
        <Home className="h-4 w-4 text-[#7042cc]" />
        <h3 className="text-sm font-bold text-gray-900">
          Best matches inside the winning area
        </h3>
      </div>
      <div className="flex flex-wrap gap-1 mb-3">
        {SCORES.map((s) => {
          const on = axis === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setAxis(s.key)}
              className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11px] font-semibold ring-1 transition-colors ${on
                ? 'bg-[#7042cc] text-white ring-[#7042cc]'
                : 'bg-white text-gray-700 ring-gray-200 hover:bg-gray-50'}`}
              data-testid={`compare-best-tab-${s.key}`}
            >
              <s.Icon className="h-3 w-3" style={{ color: on ? '#fff' : s.colour }} />
              {s.label}
            </button>
          );
        })}
      </div>
      {winnerIdx == null && (
        <p className="text-xs text-gray-500 italic">
          No sole winner on this axis yet.
        </p>
      )}
      {winnerIdx != null && loading && (
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <Loader2 className="h-3 w-3 animate-spin" /> Finding homes in {validPicks[winnerIdx].label}…
        </div>
      )}
      {winnerIdx != null && !loading && (!items || items.length === 0) && (
        <p className="text-xs text-gray-500 italic">
          No scored listings inside the winning area yet.
        </p>
      )}
      {winnerIdx != null && items && items.length > 0 && (
        <div className="grid gap-2 md:grid-cols-3">
          {items.map((p) => (
            <Link
              key={p.id || p._id}
              to={market.withPrefix(`/property/${p.id || p._id}`)}
              className="block rounded-md ring-1 ring-gray-200 p-2 hover:ring-[#7042cc] transition-colors"
              data-testid={`compare-best-item-${p.id || p._id}`}
            >
              <p className="text-xs font-semibold text-gray-900 truncate">
                {p.title || p.location?.fullAddress || 'Listing'}
              </p>
              <p className="text-[11px] text-gray-500 truncate">
                {[p.location?.area, p.location?.county].filter(Boolean).join(', ')}
              </p>
              {p.price != null && (
                <p className="text-[11px] font-mono font-bold text-[#5b35a8] mt-1">
                  €{Number(p.price).toLocaleString()}
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Public panel ──────────────────────────────────────────────────────
export const CompareNeighbourhoods = ({ variant = 'page', initialA, initialB,
                                          initialPicks, currentPropertyId }) => {
  const market = useMarket();
  const inlineMax = 2;  // drawer never grows past 2
  const [picks, setPicks] = useState(() => {
    // Prefer `initialPicks` array (used by the full page for 3-4 pin
    // URL round-trips); fall back to the older initialA/B props used
    // by the property-detail drawer.
    if (Array.isArray(initialPicks) && initialPicks.length > 0) {
      const arr = [...initialPicks];
      while (arr.length < 2) arr.push(null);
      return arr;
    }
    return [initialA || null, initialB || null];
  });
  useEffect(() => { if (initialA) setPicks((p) => { const n = [...p]; n[0] = initialA; return n; }); },
                       [initialA?.lat, initialA?.lng]);
  useEffect(() => { if (initialB) setPicks((p) => { const n = [...p]; n[1] = initialB; return n; }); },
                       [initialB?.lat, initialB?.lng]);

  const { items: dataItems, loading } = useAreaDataMany(picks);
  const validPicks = picks.filter(Boolean);
  const maxAllowed = variant === 'inline' ? inlineMax : MAX_PINS;

  const setPickAt = (i, val) => setPicks((p) => {
    const n = [...p];
    n[i] = val;
    return n;
  });
  const removePickAt = (i) => setPicks((p) => {
    const n = p.filter((_, idx) => idx !== i);
    while (n.length < 2) n.push(null);
    return n;
  });
  const addPick = () => setPicks((p) => (p.length >= maxAllowed ? p : [...p, null]));

  // Save-comparison state
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(null);
  const isAuthed = typeof window !== 'undefined' && !!window.localStorage.getItem('token');
  const canSave = validPicks.length >= 2 && !savedId && !saving;
  const saveComparison = useCallback(async () => {
    if (validPicks.length < 2) return;
    if (!isAuthed) { window.alert('Sign in to save this comparison to your account.'); return; }
    setSaving(true);
    try {
      const token = window.localStorage.getItem('token');
      // Send the full picks[] array so the backend persists 3-4 pin
      // comparisons faithfully. Legacy `a`/`b` fields are still
      // accepted by the endpoint but the array is the source of truth.
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/comparisons`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ picks: validPicks }),
      });
      const j = await r.json();
      if (r.ok && j.id) setSavedId(j.id);
    } catch { /* silent */ }
    finally { setSaving(false); }
  }, [validPicks, isAuthed]);

  // ── Share ──────────────────────────────────────────────────────────
  const shareRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const shareCardUrl = useMemo(() => {
    const p = new URLSearchParams();
    const letters = ['a', 'b', 'c', 'd'];
    validPicks.forEach((pick, i) => {
      p.set(letters[i], `${pick.lat},${pick.lng},${pick.label}`);
    });
    return `${process.env.REACT_APP_BACKEND_URL}/api/comparisons/share-card.png?${p.toString()}`;
  }, [validPicks]);

  const shareClient = async () => {
    if (validPicks.length < 2) return;
    setSharing(true);
    try {
      // Try client-side canvas via html-to-image first (WYSIWYG of
      // what the buyer sees on screen). Fall back to the server PNG
      // if the DOM snapshot fails (e.g. tainted images / CSP).
      let blob = null;
      try {
        const htmlToImage = await import('html-to-image');
        const dataUrl = await htmlToImage.toPng(shareRef.current, {
          cacheBust: true, backgroundColor: '#ffffff', pixelRatio: 2,
        });
        const res = await fetch(dataUrl);
        blob = await res.blob();
      } catch { /* fall through to server png */ }
      if (!blob) {
        const res = await fetch(shareCardUrl);
        blob = await res.blob();
      }
      const file = new File([blob], 'neighbourhood-compare.png', { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          title: 'Neighbourhood comparison',
          text:  validPicks.map((p) => p.label).join(' vs '),
          files: [file],
        });
      } else {
        // Fallback: download.
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'neighbourhood-compare.png';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch { /* silent */ }
    finally { setSharing(false); }
  };

  // ── Layout ─────────────────────────────────────────────────────────
  // 2 pins → 2 cols · 3 pins → 3 cols · 4 pins → 2×2 grid
  const cols = validPicks.length <= 2 ? 2 : validPicks.length === 3 ? 3 : 2;
  const gridCls = cols === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2';

  // Build a deep-link for the inline drawer's "More details" link.
  const fullPageHref = (() => {
    const params = new URLSearchParams();
    const letters = ['a', 'b', 'c', 'd'];
    picks.forEach((pick, i) => {
      if (pick) params.set(letters[i], `${pick.lat},${pick.lng},${pick.label}`);
    });
    if (currentPropertyId) params.set('from', currentPropertyId);
    return `/compare-neighbourhoods?${params.toString()}`;
  })();

  const allScores = dataItems.map((d) => d?.scores);

  return (
    <div data-testid={`compare-neighbourhoods-${variant}`} ref={shareRef}>
      {/* Pickers */}
      <div className={`grid gap-3 ${gridCls} mb-3`}>
        {picks.map((pick, i) => (
          <AreaPicker
            key={i}
            label={`Neighbourhood ${String.fromCharCode(65 + i)}`}
            initial={pick}
            onPick={(v) => setPickAt(i, v)}
            disabled={variant === 'inline' && i === 0 && !!initialA?.locked}
            placeholder={`e.g. ${(market?.area_example_pair || [])[i % 2] || 'Ballinteer, Dublin'}`}
          />
        ))}
      </div>

      {/* Add another */}
      {variant === 'page' && picks.length < MAX_PINS && (
        <div className="mb-3">
          <button
            type="button"
            onClick={addPick}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#5b35a8] hover:underline"
            data-testid="compare-add-area"
          >
            <Plus className="h-3 w-3" /> Add another neighbourhood ({picks.length}/{MAX_PINS})
          </button>
        </div>
      )}

      {/* Columns */}
      <div className={`grid gap-3 ${gridCls}`}>
        {picks.map((pick, i) => (
          <AreaColumn
            key={i}
            pick={pick}
            data={dataItems[i]}
            allScores={allScores}
            index={i}
            allowRemove={variant === 'page' && picks.length > 2}
            onRemove={() => removePickAt(i)}
          />
        ))}
      </div>

      {/* Save + Share (page only) */}
      {variant === 'page' && validPicks.length >= 2 && (
        <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
          <p className="text-[11px] text-gray-500">
            {savedId ? (
              <>
                Saved to your account.{' '}
                <Link
                  to={market.withPrefix('/my-comparisons')}
                  className="text-[#7042cc] hover:underline font-semibold"
                  data-testid="compare-view-saved-link"
                >
                  View all saved comparisons →
                </Link>
              </>
            ) : (
              'Save this comparison to revisit later.'
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={shareClient}
              disabled={sharing || loading}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold ring-1 bg-white text-gray-700 ring-gray-300 hover:bg-gray-50 disabled:opacity-40"
              data-testid="compare-share-btn"
              title="Share as image (WhatsApp, iMessage, download)"
            >
              {sharing
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <><Share2 className="h-3 w-3" /> Share</>}
            </button>
            <a
              href={shareCardUrl}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold ring-1 bg-white text-gray-700 ring-gray-300 hover:bg-gray-50"
              data-testid="compare-share-link"
              title="Open a shareable PNG in a new tab"
            >
              <Download className="h-3 w-3" /> Card link
            </a>
            <button
              type="button"
              onClick={saveComparison}
              disabled={!canSave}
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-semibold ring-1 transition-colors ${savedId
                ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 cursor-default'
                : 'bg-[#7042cc] text-white ring-[#7042cc] hover:bg-[#5b35a8] disabled:opacity-40'}`}
              data-testid="compare-save-btn"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin" />
                : savedId ? <><Check className="h-3 w-3" /> Saved</>
                            : <><Save className="h-3 w-3" /> Save</>}
            </button>
          </div>
        </div>
      )}

      {/* Best-match strip (page only, ≥2 pins) */}
      {variant === 'page' && (
        <BestMatchStrip picks={picks} dataItems={dataItems} />
      )}

      {variant === 'inline' && (
        <div className="mt-3 text-right">
          <Link
            to={fullPageHref}
            className="inline-flex items-center gap-1 text-xs font-semibold text-[#5b35a8] hover:underline"
            data-testid="compare-open-full-page"
          >
            More details · full comparison <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}
    </div>
  );
};

export default CompareNeighbourhoods;
